import { useState, type ReactNode } from 'react';

// Shared note-sharing UI (#24) — the V38 "share with / View Only · Can Edit"
// controls used by BOTH the contact Notes tab and the Tasks-tab task notes, so
// the two behave identically. Panel-specific bits (composer state, timeline row)
// live in each tab; the reusable primitives live here.

/** Per-note share access level (View Only vs Can Edit). */
export type NoteAccess = 'view' | 'edit';

/** One note-share instruction sent to the API: user + access level. */
export interface NoteShare {
  userId: string;
  access: NoteAccess;
}

/** A user that can author or receive a shared note. */
export interface NoteUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role?: string;
  /** Default reviewer for this user's notes (users.supervisorId; from /settings/users). */
  supervisorId?: string | null;
}

/** The minimal note shape the sharing controls need. */
export interface ShareableNote {
  sharedWith: string[];
  sharedAccess: Record<string, NoteAccess>;
}

const NS_AVATAR_COLORS = [
  '#7c3aed',
  '#0c7be0',
  '#0e7490',
  '#d97706',
  '#1a8a66',
  '#c0392b',
  '#527060',
];
export const nsAvatarColor = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return NS_AVATAR_COLORS[h % NS_AVATAR_COLORS.length];
};
export const nsInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';
const NS_ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  supervisor: 'Supervisor',
  clinician: 'Clinician',
  front_desk: 'Front Desk',
};
export const nsRoleLabel = (role?: string) => NS_ROLE_LABEL[role ?? ''] ?? role ?? '';
export const nsUserName = (u: NoteUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
/** A note's current share set as API instructions ({userId, access}). */
export const noteShares = (n: ShareableNote): NoteShare[] =>
  n.sharedWith.map((id) => ({ userId: id, access: n.sharedAccess[id] ?? 'edit' }));

/** Three-circles "share" glyph (the share link + trigger). */
export const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="13"
    height="13"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

/** Box + up-arrow glyph used on the "Shared with / Shared by" badges. */
export const UploadShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="10"
    height="10"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    aria-hidden
  >
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);

/** Optional info banner above the share list (contact notes explain the gating). */
export function ShareHint({ text }: { text: ReactNode }) {
  return (
    <div className="note-share-hint">
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      {text}
    </div>
  );
}

/** Inline View Only / Can Edit access toggle — verbatim hex from the V38 mockup
 *  (hard-coded, not CSS vars, so both share surfaces match pixel-for-pixel). */
export function NoteAccessToggle({
  value,
  onChange,
}: {
  value: NoteAccess;
  onChange: (v: NoteAccess) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        border: '1.5px solid var(--border)',
        borderRadius: 7,
        overflow: 'hidden',
        marginBottom: 8,
        width: 'fit-content',
      }}
    >
      {(['view', 'edit'] as const).map((lvl, i) => (
        <button
          key={lvl}
          type="button"
          onClick={() => onChange(lvl)}
          style={{
            padding: '4px 12px',
            fontFamily: "'Sora', sans-serif",
            fontSize: '11.5px',
            fontWeight: 600,
            border: 'none',
            borderRight: i === 0 ? '1.5px solid var(--border)' : undefined,
            cursor: 'pointer',
            background: value === lvl ? '#1a8a66' : 'var(--white)',
            color: value === lvl ? '#fff' : 'var(--text-muted)',
          }}
        >
          {lvl === 'view' ? 'View Only' : 'Can Edit'}
        </button>
      ))}
    </div>
  );
}

/**
 * Per-note "Share this note with" popover (V38). Anchored under the note's share
 * link. Sharing takes effect on each checkbox toggle (add immediately; removing a
 * shared user is confirmed by the parent). Already-shared rows show their access.
 */
export function NoteSharePopover({
  note,
  candidates,
  myId,
  hint,
  emptyText = 'No other users have access to this contact yet.',
  onAdd,
  onRemove,
  onCancelAll,
  onClose,
}: {
  note: ShareableNote;
  candidates: NoteUser[];
  myId: string | undefined;
  /** Optional info banner (contact notes explain the contact-share gating). */
  hint?: ReactNode;
  /** Empty-state copy when there are no candidates. */
  emptyText?: string;
  onAdd: (userId: string, access: NoteAccess) => void;
  onRemove: (userId: string, name: string) => void;
  onCancelAll: () => void;
  onClose: () => void;
}) {
  const [access, setAccess] = useState<NoteAccess>('edit');
  const [q, setQ] = useState('');
  const shared = new Set(note.sharedWith);
  const pool = candidates.filter((u) => u.id !== myId);
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? pool.filter(
        (u) =>
          nsUserName(u).toLowerCase().includes(needle) ||
          (u.role ?? '').toLowerCase().includes(needle),
      )
    : pool;
  return (
    <div
      className="note-share-picker note-share-pop"
      role="dialog"
      aria-label="Share this note with"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="note-share-picker-head">Share this note with</div>
      {hint && <ShareHint text={hint} />}
      <NoteAccessToggle value={access} onChange={setAccess} />
      {pool.length > 1 && (
        <div className="note-share-search-wrap">
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="note-share-search"
            type="search"
            placeholder="Search users…"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      )}
      <div>
        {pool.length === 0 ? (
          <div className="note-share-empty">{emptyText}</div>
        ) : filtered.length === 0 ? (
          <div className="note-share-empty">No users match your search.</div>
        ) : (
          filtered.map((u) => {
            const on = shared.has(u.id);
            const nm = nsUserName(u);
            const acc = note.sharedAccess[u.id];
            return (
              <label key={u.id} className="note-share-user-row">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => (on ? onRemove(u.id, nm) : onAdd(u.id, access))}
                />
                <span className="user-avatar tiny" style={{ background: nsAvatarColor(u.id) }}>
                  {nsInitials(nm)}
                </span>
                <span className="note-share-user-name">
                  {nm}
                  {on && (
                    <em className="note-share-user-access">
                      {' '}
                      ({acc === 'view' ? 'view only' : 'can edit'})
                    </em>
                  )}
                </span>
                <span className="note-share-user-role">{nsRoleLabel(u.role)}</span>
              </label>
            );
          })
        )}
      </div>
      <div className="note-share-pop-foot">
        {note.sharedWith.length > 0 ? (
          <button type="button" className="note-cancel-share-btn" onClick={onCancelAll}>
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            Cancel Sharing
          </button>
        ) : (
          <span />
        )}
        <button type="button" className="btn ghost-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
